// Reusable, data-agnostic tree component for worksnip. Renders a flat list
// of normalized TreeNode rows with hairline connector rails (V1 elbow /
// mid / pass-through algorithm), drag/drop with three-zone edge detection
// (before / after / into), per-row right-click + ⋯ context menu (portaled),
// inline rename + new-folder editors, count badges, and per-node icon slot.
//
// What this component owns:
//   • Layout + connector rails (depth, indent, icon-center math)
//   • Drag-and-drop event plumbing → fires `onDragDrop({srcId,dstId,edge})`
//   • Right-click + ⋯ button → opens portaled <TreeRowContextMenu>
//   • Inline rename + new-folder input wiring
//   • Keyboard focus management on context menu
//
// What it does NOT own:
//   • Data loading / API calls (consumer maps its domain → TreeNode[])
//   • Expand/collapse state (consumer passes `expandedIds` + `onToggleExpand`)
//   • Selection state (consumer passes `selectedId` + `onSelect`)
//
// See `tree.css` for the visual rules ported from the Snip Board prototype.

import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ChevronRight, GripVertical, MoreHorizontal, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TreeRowContextMenu, type TreeMenuAction } from './TreeRowContextMenu';
import './tree.css';

export type TreeNodeKind = 'project' | 'folder' | 'leaf' | 'diagram';
export type TreeDropEdge = 'before' | 'after' | 'into';

export interface TreeNode {
  id: string;
  parentId: string | null;
  depth: number;
  kind: TreeNodeKind;
  label: string;
  /** Optional colored tint applied to the icon (project color, kind color, etc). */
  iconColor?: string;
  /** Optional badge number (folder rolling guide-count). */
  count?: number;
  /** Optional open-thread count — rendered as a distinct 💬 badge on leaves
   *  so snips with live conversations stand out in the tree (BP 22/03). */
  threadCount?: number;
  /** Optional mono-font public id pill (SNIP_xxxx). */
  publicId?: string;
  /** BP 37 — the snip's typed-kind slug (on leaf rows). Lets the consumer's
   *  renderIcon show a kind-colored icon for issues/demos/rfcs/etc. */
  snipKind?: string;
  /** Optional outline number ("1.1.1" / "2.3") rendered as a mono prefix to
   *  the label. Inherited from the ProjectTree's display-numbering feature. */
  displayNumber?: string;
  /** Has visible children — drives chevron vs leaf-dot. */
  hasChildren?: boolean;
  /** Disable drag-from for this row (e.g. synthetic "All snips" header). */
  noDrag?: boolean;
  /** Disable drop-into for this row. */
  noDropInto?: boolean;
  /** Disable any drop (before/after/into) — useful for synthetic rows. */
  noDrop?: boolean;
}

interface TreeViewProps {
  nodes: TreeNode[];
  expandedIds: Set<string> | ReadonlyArray<string>;
  selectedId?: string | null;
  /** When true, drag handles + ⋯ buttons + folder context menus become visible. */
  editMode?: boolean;
  /** Visual density variant. 'rail' (default) = the 312px left-rail look.
   *  'wide' = the Projects Tree main panel: taller rows, always-on public-id
   *  pills + count badges reading comfortably at full page width. */
  variant?: 'rail' | 'wide';
  /** Optional fuzzy-search highlight indices per node id (for <mark> spans). */
  searchMatchIdx?: Record<string, number[]>;
  /** ID of the row whose label is being renamed inline. */
  inlineEditId?: string | null;
  inlineEditValue?: string;
  /** ID of the parent under which a new-folder editor row should appear. */
  inlineNewFolderParentId?: string | null;
  /** Optional initial value for the new-folder editor. */
  inlineNewFolderValue?: string;

  onSelect?: (id: string) => void;
  /** Click the per-leaf open-threads badge (overlaid on the icon). */
  onThreadClick?: (id: string) => void;
  onToggleExpand?: (id: string) => void;
  onDragDrop?: (drop: { srcId: string; dstId: string; edge: TreeDropEdge }) => void;
  onSubmitRename?: (id: string, name: string) => void;
  onCancelRename?: () => void;
  onSubmitNewFolder?: (parentId: string | null, name: string) => void;
  onCancelNewFolder?: () => void;
  /** Open the row's context menu via keyboard (Menu / F10 / Shift+F10). */
  onContextAction?: (nodeId: string, actionId: string) => void;
  /** Builder for the per-row context-menu actions. Return [] to skip the menu. */
  getContextActions?: (node: TreeNode) => TreeMenuAction[];
  /** Icon renderer for a row. Falls back to no-op if omitted. */
  renderIcon?: (node: TreeNode) => ReactNode;
  /** Optional scroll container ref used to close the floating menu on scroll. */
  scrollContainerRef?: React.RefObject<HTMLElement>;

  className?: string;
  /** Optional ARIA label for the tree itself. */
  ariaLabel?: string;
}

// Layout constants — keep in sync with tree.css comment header.
const LAYOUT = {
  PAD_LEFT: 12,
  INDENT: 28,
  CHEV: 16,
  ICON_GAP: 4,
  ICON_W: 16,
};
const iconCenterX = (d: number) =>
  LAYOUT.PAD_LEFT + d * LAYOUT.INDENT + LAYOUT.CHEV + LAYOUT.ICON_GAP + LAYOUT.ICON_W / 2;

export function TreeView({
  nodes,
  expandedIds,
  selectedId,
  editMode = false,
  variant = 'rail',
  searchMatchIdx,
  inlineEditId = null,
  inlineEditValue,
  // Tri-state: undefined = no editor, null = root-level editor, string =
  // editor under that parent. Default must be undefined or consumer state
  // collapses to "always show root editor". See treeview-implementation.md §6.
  inlineNewFolderParentId,
  inlineNewFolderValue,
  onSelect,
  onThreadClick,
  onToggleExpand,
  onDragDrop,
  onSubmitRename,
  onCancelRename,
  onSubmitNewFolder,
  onCancelNewFolder,
  onContextAction,
  getContextActions,
  renderIcon,
  scrollContainerRef,
  className,
  ariaLabel,
}: TreeViewProps) {
  const { t } = useTranslation('projects');
  const resolvedAriaLabel = ariaLabel ?? t('tree.view.defaultAriaLabel');
  // — last-sibling / ancestor lookup, used by the connector-rail algorithm —
  const { byId, isLastSibling, ancestorsOf } = useMemo(() => {
    const map = new Map<string, TreeNode>();
    for (const n of nodes) map.set(n.id, n);

    // groupLastByParent: rightmost (last) visible node per parent key.
    const lastByParent = new Map<string, string>();
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i];
      const key = n.parentId || '__root';
      if (!lastByParent.has(key)) lastByParent.set(key, n.id);
    }
    const isLast = (id: string) => {
      const n = map.get(id);
      if (!n) return true;
      return lastByParent.get(n.parentId || '__root') === id;
    };

    const cache = new Map<string, TreeNode[]>();
    const anc = (id: string): TreeNode[] => {
      const cached = cache.get(id);
      if (cached) return cached;
      const list: TreeNode[] = [];
      let cur = map.get(id);
      while (cur && cur.parentId) {
        const par = map.get(cur.parentId);
        if (!par) break;
        list.unshift(par);
        cur = par;
      }
      cache.set(id, list);
      return list;
    };

    return { byId: map, isLastSibling: isLast, ancestorsOf: anc };
  }, [nodes]);

  // — Drag-and-drop state —————————————————————————————————————————————
  const [dragSrcId, setDragSrcId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; edge: TreeDropEdge } | null>(null);

  // Compute which third of the row the cursor is in. Folders/projects get
  // a three-zone (before / into / after); leaves get two zones (before/after).
  function computeEdge(node: TreeNode, e: React.DragEvent<HTMLDivElement>): TreeDropEdge {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const ratio = (e.clientY - rect.top) / rect.height;
    const allowInto = !node.noDropInto && (node.kind === 'project' || node.kind === 'folder');
    if (allowInto) {
      if (ratio < 0.33) return 'before';
      if (ratio > 0.67) return 'after';
      return 'into';
    }
    return ratio < 0.5 ? 'before' : 'after';
  }

  function onRowDragStart(node: TreeNode, e: React.DragEvent<HTMLDivElement>) {
    if (!editMode || node.noDrag) {
      e.preventDefault();
      return;
    }
    setDragSrcId(node.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', node.id);
  }
  function onRowDragOver(node: TreeNode, e: React.DragEvent<HTMLDivElement>) {
    if (node.noDrop) return;
    // BP W9-8c — accept EXTERNAL drags too (guide cards from CapturesPage,
    // or any future surface that drops onto the tree). Such drags don't
    // fire onRowDragStart, so `dragSrcId` is null — the old guard at
    // `if (!dragSrcId) return` killed every cross-component drop and
    // showed the "no-drop" cursor. We detect an external drag by the
    // presence of a `text/plain` payload (set by CapturesPage's
    // onDragStart, by the synthetic Inbox/All rows in ProjectTree, and
    // by anything else following the same convention).
    const isInternal  = !!dragSrcId;
    const isExternal  = !isInternal && e.dataTransfer.types.includes('text/plain');
    if (!isInternal && !isExternal) return;
    if (isInternal && dragSrcId === node.id) return;
    // For internal drags only: forbid dropping a node INSIDE its own
    // subtree. External drags can't possibly hit this because the source
    // doesn't live in the tree at all.
    if (isInternal) {
      let cur: TreeNode | undefined = node;
      while (cur) {
        if (cur.id === dragSrcId) return;
        cur = cur.parentId ? byId.get(cur.parentId) : undefined;
      }
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // External drags always land INSIDE the target (a guide card onto a
    // folder = "put this guide in that folder"). Internal drags compute
    // edge from cursor position (before / after / into).
    const edge = isExternal ? 'into' : computeEdge(node, e);
    if (!dropTarget || dropTarget.id !== node.id || dropTarget.edge !== edge) {
      setDropTarget({ id: node.id, edge });
    }
  }
  function onRowDragLeave(node: TreeNode) {
    if (dropTarget && dropTarget.id === node.id) setDropTarget(null);
  }
  function onRowDrop(node: TreeNode, e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    // BP W9-8c — for external drags, srcId comes from the payload, not
    // from `dragSrcId` (which never got set because the drag started
    // outside the tree).
    const src = dragSrcId || e.dataTransfer.getData('text/plain') || null;
    const target = dropTarget;
    setDragSrcId(null);
    setDropTarget(null);
    if (!src || !target || src === target.id) return;
    onDragDrop?.({ srcId: src, dstId: target.id, edge: target.edge });
  }
  function onAnyDragEnd() {
    setDragSrcId(null);
    setDropTarget(null);
  }

  // — Context menu state —————————————————————————————————————————————
  const [menuFor, setMenuFor] = useState<
    | { id: string; variant: 'anchor' }
    | { id: string; variant: 'float'; pos: { x: number; y: number }; returnFocusTo: HTMLElement | null }
    | null
  >(null);

  function openAnchoredMenu(id: string) {
    setMenuFor({ id, variant: 'anchor' });
  }
  function openFloatMenu(id: string, ev: React.MouseEvent, returnFocusTo: HTMLElement | null) {
    ev.preventDefault();
    setMenuFor({ id, variant: 'float', pos: { x: ev.clientX, y: ev.clientY }, returnFocusTo });
  }
  function closeMenu() { setMenuFor(null); }
  function dispatchMenuAction(actionId: string) {
    if (!menuFor) return;
    onContextAction?.(menuFor.id, actionId);
  }

  // Keyboard: F2 rename, Delete delete, Menu key opens anchored menu for selected.
  // Gated on the row's OWN context-menu actions (not just its kind) so a kind
  // that opts out of a menu entirely — e.g. 'diagram' rows, whose mutations
  // belong to the PageSnip page, not this shared tree — can't be rewired via
  // a stray keypress into calling an action the row never offered.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!editMode || !selectedId) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const node = byId.get(selectedId);
      if (!node) return;
      const actions = getContextActions ? getContextActions(node) : [];
      if (e.key === 'F2') {
        if (actions.some((a) => a.id === 'rename')) onContextAction?.(selectedId, 'rename');
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if ((node.kind === 'folder' || node.kind === 'project') && actions.some((a) => a.id === 'delete')) {
          onContextAction?.(selectedId, 'delete');
        }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editMode, selectedId, byId, onContextAction, getContextActions]);

  // Inline rename submit/cancel handler shared by rename + new-folder inputs.
  const handleInputKey = useCallback(
    (
      onSubmit: (val: string) => void,
      onCancel: () => void,
    ) =>
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const v = e.currentTarget.value.trim();
        if (!v) { onCancel(); return; }
        onSubmit(v);
      } else if (e.key === 'Escape') {
        onCancel();
      }
    },
    [],
  );

  // — Expanded-id Set normalization (accept Set or Array) —
  const expanded = useMemo(
    () => (expandedIds instanceof Set ? expandedIds : new Set(expandedIds)),
    [expandedIds],
  );

  // — Render —————————————————————————————————————————————————————————
  // We render with a single map; the inline new-folder editor row is
  // appended after the last visible descendant of its target parent.
  const newFolderInsertAfterId = useMemo(() => {
    if (inlineNewFolderParentId == null) return null;
    let last: string = inlineNewFolderParentId;
    for (const r of nodes) {
      let cur: TreeNode | undefined = r;
      while (cur && cur.parentId) {
        if (cur.parentId === inlineNewFolderParentId) { last = r.id; break; }
        cur = byId.get(cur.parentId);
      }
    }
    return last;
  }, [inlineNewFolderParentId, nodes, byId]);

  return (
    <div
      className={`ws-tree ${variant === 'wide' ? 'ws-tree--wide' : ''} ${editMode ? 'is-admin' : ''} ${className || ''}`}
      role="tree"
      aria-label={resolvedAriaLabel}
    >
      {nodes.map((n) => {
        const padLeft = LAYOUT.PAD_LEFT + n.depth * LAYOUT.INDENT;
        const ancestors = ancestorsOf(n.id);

        // Connector rails — one span per depth slot.
        const rails: ReactNode[] = [];
        for (let d = 0; d < n.depth; d++) {
          const relevant: TreeNode | undefined =
            d + 1 === n.depth ? n : ancestors[d + 1];
          const last = relevant ? isLastSibling(relevant.id) : true;
          let state: 'last' | 'mid' | 'thru' | null;
          if (d === n.depth - 1) state = last ? 'last' : 'mid';
          else                    state = last ? null   : 'thru';
          if (state) {
            const x = iconCenterX(d);
            rails.push(
              <span
                key={`r-${d}`}
                className={`ws-rail is-${state}`}
                style={{ left: x }}
                aria-hidden
              />,
            );
          }
        }

        const isExpanded = expanded.has(n.id);
        const isActive = selectedId === n.id;
        const isRenaming = inlineEditId === n.id;
        const showHoverMenu =
          editMode && (n.kind === 'project' || n.kind === 'folder');
        const cls = [
          'ws-tnode',
          isExpanded ? 'is-open' : '',
          isActive ? 'is-active' : '',
          isRenaming ? 'is-editing' : '',
          dragSrcId === n.id ? 'is-dragging' : '',
          dropTarget && dropTarget.id === n.id ? `drop-${dropTarget.edge}` : '',
        ].filter(Boolean).join(' ');

        const label =
          searchMatchIdx && searchMatchIdx[n.id]
            ? renderHighlight(n.label, searchMatchIdx[n.id])
            : n.label;

        const dragHandle = !n.noDrag && editMode ? (
          <span
            className="drag"
            draggable
            onDragStart={(e) => onRowDragStart(n, e as unknown as React.DragEvent<HTMLDivElement>)}
            onDragEnd={onAnyDragEnd}
            aria-hidden
          >
            <GripVertical size={12} strokeWidth={2} />
          </span>
        ) : null;

        const chev = n.hasChildren ? (
          <button
            type="button"
            className="chev"
            onClick={(e) => { e.stopPropagation(); onToggleExpand?.(n.id); }}
            aria-label={isExpanded ? t('tree.view.collapse') : t('tree.view.expand')}
          >
            <ChevronRight size={10} strokeWidth={2.5} />
          </button>
        ) : (
          <span className="leaf-dot" aria-hidden />
        );

        const iconNode = renderIcon ? renderIcon(n) : null;
        const hasThreads = n.threadCount != null && n.threadCount > 0;
        // Open-thread badge is overlaid on the icon's corner (notification-dot
        // style) so it doesn't crowd the label, and is CLICKABLE to jump to the
        // snip's threads.
        const iconWrap = (
          <span
            className={`ic ${n.kind}`}
            style={n.iconColor ? { color: n.iconColor } : undefined}
          >
            <span className="ic-glyph" aria-hidden>{iconNode}</span>
            {hasThreads && (
              <button
                type="button"
                className="ws-tnode__thread-dot"
                title={t('tree.view.openThreadsTitle', { count: n.threadCount })}
                onClick={(e) => { e.stopPropagation(); onThreadClick?.(n.id); }}
              >
                {n.threadCount! > 9 ? '9+' : n.threadCount}
              </button>
            )}
          </span>
        );

        const numNode = n.count != null && n.count > 0 ? (
          <span className="num">{n.count}</span>
        ) : null;
        const publicIdNode = n.publicId ? (
          <span className="public-id">{n.publicId}</span>
        ) : null;

        const actions = getContextActions ? getContextActions(n) : [];

        const menuBtn = showHoverMenu && actions.length ? (
          <button
            type="button"
            className="ws-tnode__menu"
            onClick={(e) => {
              e.stopPropagation();
              openAnchoredMenu(n.id);
            }}
            aria-label={t('tree.view.rowActions')}
            title={t('tree.view.rowActions')}
          >
            <MoreHorizontal size={13} />
          </button>
        ) : null;

        if (isRenaming) {
          // Inline rename row — replaces the label slot with an <input>.
          return (
            <div
              key={n.id}
              className={cls}
              style={{ paddingLeft: padLeft }}
              data-node={n.id}
            >
              {rails}
              {chev}
              {iconWrap}
              <input
                autoFocus
                defaultValue={inlineEditValue ?? n.label}
                className="ws-tnode__input"
                onKeyDown={handleInputKey(
                  (v) => onSubmitRename?.(n.id, v),
                  () => onCancelRename?.(),
                )}
                onBlur={(e) => {
                  const v = e.currentTarget.value.trim();
                  if (v && v !== n.label) onSubmitRename?.(n.id, v);
                  else onCancelRename?.();
                }}
              />
              <button
                type="button"
                className="ws-tnode__cancel"
                onMouseDown={(e) => { e.preventDefault(); onCancelRename?.(); }}
                aria-label={t('tree.view.cancelRename')}
                title={t('tree.view.cancelEsc')}
              >
                <X size={11} />
              </button>
            </div>
          );
        }

        const row = (
          <div
            key={n.id}
            className={cls}
            style={{ paddingLeft: padLeft }}
            data-node={n.id}
            title={n.label}
            onClick={() => onSelect?.(n.id)}
            onContextMenu={(e) => {
              if (actions.length) {
                openFloatMenu(n.id, e, e.currentTarget as HTMLElement);
              }
            }}
            onDragOver={(e) => onRowDragOver(n, e)}
            onDragLeave={() => onRowDragLeave(n)}
            onDrop={(e) => onRowDrop(n, e)}
            onDragEnd={onAnyDragEnd}
            role="treeitem"
            aria-selected={isActive}
            aria-expanded={n.hasChildren ? isExpanded : undefined}
            aria-level={n.depth + 1}
          >
            {rails}
            {dragHandle}
            {chev}
            {iconWrap}
            {n.displayNumber && (
              <span className="display-num" aria-hidden>{n.displayNumber}</span>
            )}
            <span className="lbl">{label}</span>
            {publicIdNode}
            {numNode}
            {menuBtn}
            {menuFor && menuFor.id === n.id && menuFor.variant === 'anchor' && (
              <TreeRowContextMenu
                variant="anchor"
                actions={actions}
                onAction={dispatchMenuAction}
                onClose={closeMenu}
              />
            )}
          </div>
        );

        // If this is the row after which the new-folder editor goes, append it.
        // CRITICAL: wrapper MUST be a Fragment, NOT a <span>. A span is
        // inline-flow and would break the flex/block layout of sibling rows.
        // See docs/BLUEPRINTS/22-threads-feature/treeview-implementation.md §6.
        if (
          inlineNewFolderParentId != null &&
          newFolderInsertAfterId === n.id
        ) {
          const editorDepth = (byId.get(inlineNewFolderParentId)?.depth ?? -1) + 1;
          const editorPadLeft = LAYOUT.PAD_LEFT + editorDepth * LAYOUT.INDENT;
          const editorRail = editorDepth > 0 ? (
            <span
              className="ws-rail is-last"
              style={{ left: iconCenterX(editorDepth - 1) }}
              aria-hidden
            />
          ) : null;
          return (
            <Fragment key={`${n.id}-w`}>
              {row}
              <div
                className="ws-tnode is-editing"
                data-new-folder-parent={inlineNewFolderParentId}
                style={{ paddingLeft: editorPadLeft }}
              >
                {editorRail}
                <span className="leaf-dot" aria-hidden />
                <span className="ic folder" aria-hidden>{renderIcon?.({ ...n, kind: 'folder' })}</span>
                <input
                  autoFocus
                  className="ws-tnode__input"
                  placeholder={t('tree.view.newFolderPlaceholder')}
                  defaultValue={inlineNewFolderValue || ''}
                  onKeyDown={handleInputKey(
                    (v) => onSubmitNewFolder?.(inlineNewFolderParentId, v),
                    () => onCancelNewFolder?.(),
                  )}
                  onBlur={(e) => {
                    const v = e.currentTarget.value.trim();
                    if (v) onSubmitNewFolder?.(inlineNewFolderParentId, v);
                    else onCancelNewFolder?.();
                  }}
                />
                <button
                  type="button"
                  className="ws-tnode__cancel"
                  onMouseDown={(e) => { e.preventDefault(); onCancelNewFolder?.(); }}
                  aria-label={t('tree.view.cancelNewFolder')}
                  title={t('tree.view.cancelEsc')}
                >
                  <X size={11} />
                </button>
              </div>
            </Fragment>
          );
        }

        return row;
      })}

      {/* Root-level inline new-folder editor (parent === null) — pinned to the top. */}
      {inlineNewFolderParentId === null && (
        <div
          className="ws-tnode is-editing"
          data-new-folder-parent="__root"
          style={{ paddingLeft: LAYOUT.PAD_LEFT }}
        >
          <span className="leaf-dot" aria-hidden />
          <span className="ic folder" aria-hidden />
          <input
            autoFocus
            className="ws-tnode__input"
            placeholder={t('tree.view.newFolderPlaceholder')}
            defaultValue={inlineNewFolderValue || ''}
            onKeyDown={handleInputKey(
              (v) => onSubmitNewFolder?.(null, v),
              () => onCancelNewFolder?.(),
            )}
            onBlur={(e) => {
              const v = e.currentTarget.value.trim();
              if (v) onSubmitNewFolder?.(null, v);
              else onCancelNewFolder?.();
            }}
          />
          <button
            type="button"
            className="ws-tnode__cancel"
            onMouseDown={(e) => { e.preventDefault(); onCancelNewFolder?.(); }}
            aria-label={t('tree.view.cancelNewFolder')}
            title={t('tree.view.cancelEsc')}
          >
            <X size={11} />
          </button>
        </div>
      )}

      {/* Floating context menu — portaled to <body>. */}
      {menuFor && menuFor.variant === 'float' && (() => {
        const node = byId.get(menuFor.id);
        if (!node) return null;
        const actions = getContextActions ? getContextActions(node) : [];
        if (!actions.length) return null;
        return (
          <TreeRowContextMenu
            variant="float"
            pos={menuFor.pos}
            actions={actions}
            onAction={dispatchMenuAction}
            onClose={closeMenu}
            returnFocusTo={menuFor.returnFocusTo}
            scrollContainer={scrollContainerRef?.current ?? null}
          />
        );
      })()}
    </div>
  );
}

// — fuzzy-match highlighting: take the label + matched char indices and
// wrap each matched character in <mark>. Indices are 0-based positions.
function renderHighlight(label: string, idx: number[]): ReactNode {
  if (!idx || !idx.length) return label;
  const set = new Set(idx);
  const out: ReactNode[] = [];
  let buf = '';
  let isMark = false;
  for (let i = 0; i < label.length; i++) {
    const inMark = set.has(i);
    if (inMark !== isMark) {
      if (buf) out.push(isMark ? <mark key={`m${i}`}>{buf}</mark> : buf);
      buf = '';
      isMark = inMark;
    }
    buf += label[i];
  }
  if (buf) out.push(isMark ? <mark key="m-last">{buf}</mark> : buf);
  return <>{out}</>;
}
